import React, { ChangeEventHandler, MouseEventHandler, useEffect, useState, useRef, useCallback } from "react";
import { ConnectionLog } from "../common/ObjectTypes";
import LogsFilter from "../activityLogs/ActivityLogsFilter";
import { ArrowDown, ArrowRight, RefreshSpinner, SortedDown, SortedUp } from "../common/ProjectIcons";
import Spinner from "../common/Spinner";
import { useTimezone } from "../../context/TimezoneContext";
import { getUtcOffsets } from "../common/getUtcOffsets";
import { useTranslation } from "react-i18next";
import NoResults from "../common/NoResults";
import { useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { tableClasses } from "../common/tailwindClassnames";

type ConnectionLogProps = {
    connectionHasDestinations: boolean,
    logs: ConnectionLog[],
    reloadLogs: boolean,
    prevCursor: string | null,
    nextCursor: string | null,
    selectedTimezone: any,
    onSelectTimezone: Function,
    onNextPageClicked: Function,
    onPrevPageClicked: MouseEventHandler,
    requestSort: Function,
    pageLoaded: boolean,
    filters: any[],
    globalSearchStr: string,
    onChangeLogGlobalSearch: Function,
    onChangeLogStatus: ChangeEventHandler,
    onChangeLogType: ChangeEventHandler,
    onChangeLogDate: Function,
    onRemoveLogFilterTag: Function,
    onResetLogFilters: Function,
    onSelectLog: Function,
    onRefreshLogs: Function,
    isFromAddConnectionFlow: boolean
    skippedAddingDestinations: boolean
    logsError?: boolean
}

const getClassname = (log_level: number) => {
    switch (log_level) {
        case 10:
            return "text-xs text-center rounded rounded-sm bg-light-blue dark:bg-blue-900 text-blue-basic dark:text-blue-300 transition-colors duration-300";
        case 20:
            return "text-xs text-center rounded rounded-sm bg-light-green dark:bg-dark-green-900 text-dark-green dark:text-dark-green-100 transition-colors duration-300";
        case 30:
            return "text-xs text-center rounded rounded-sm bg-warning-yellow dark:bg-warning-dark-yellow dark:bg-opacity-20 text-warning-dark-yellow dark:text-warning-dark-yellow transition-colors duration-300";
        case 40:
            return "text-xs text-center rounded rounded-sm bg-very-light-orange dark:bg-orange-900 text-orange dark:text-orange-300 transition-colors duration-300";
        default:
            return "text-xs text-center rounded rounded-sm bg-light-gray dark:bg-neutral-gray-700 text-medium-gray dark:text-neutral-gray-400 transition-colors duration-300";
    }

}

const getLogLevelText = (log_level: number) => {
    switch (log_level) {
        case 10:
            return "Debug";
        case 20:
            return "Info";
        case 30:
            return "Warning";
        case 40:
            return "Error";
        default:
            return "--";
    }
}

const ConnectionDetailsLog = ({
    connectionHasDestinations,
    logs,
    reloadLogs,
    prevCursor,
    nextCursor,
    selectedTimezone,
    onSelectTimezone,
    onNextPageClicked,
    onPrevPageClicked,
    requestSort,
    pageLoaded,
    filters,
    globalSearchStr,
    onChangeLogGlobalSearch,
    onChangeLogStatus,
    onChangeLogType,
    onChangeLogDate,
    onRemoveLogFilterTag,
    onResetLogFilters,
    onSelectLog,
    onRefreshLogs,
    isFromAddConnectionFlow,
    skippedAddingDestinations,
    logsError = false
}: ConnectionLogProps) => {
    const { t } = useTranslation();
    const { connection_id } = useParams();
    const navigate = useNavigate();
    const { timezone } = useTimezone();
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [timezones, setTimezones] = useState<any[]>([]);
    const [sortDirection, setSortDirection] = useState<boolean>(true);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [logDetails, setLogDetails] = useState<ConnectionLog | null>(null);

    // Memoize scroll handler, do not depend on nextCursor
    const handleScroll = useCallback(() => {
        if (!tableContainerRef.current || reloadLogs) return;

        // Save current scroll position
        const currentScrollTop = tableContainerRef.current.scrollTop;
        setScrollPosition(currentScrollTop);

        const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;

        // Only trigger next page load if we're very close to the bottom (within 20px)
        // and there's more content to load
        if (scrollTop + clientHeight >= scrollHeight - 20 && nextCursor) {
            onNextPageClicked(); // Load next page
        }
    }, [reloadLogs, onNextPageClicked, nextCursor]);

    // Debounced handler ref
    const scrollTimeoutRef = useRef<number | null>(null);
    const debouncedScrollHandler = useCallback(() => {
        if (scrollTimeoutRef.current) {
            window.clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = window.setTimeout(() => {
            handleScroll();
        }, 50);
    }, [handleScroll]);
    const formatDatetime = (datetime: string) => {
        const date = new Date(datetime);

        // Parse timezone offset
        const sign = timezone.value.startsWith("-") ? -1 : 1;
        const hours = parseInt(timezone.value.slice(1, 3), 10);
        const minutes = parseInt(timezone.value.slice(3, 5), 10) || 0;

        const offsetMilliseconds = sign * (hours * 60 + minutes) * 60 * 1000;

        const adjustedDate = new Date(date.getTime() + offsetMilliseconds);

        const year = adjustedDate.getUTCFullYear();
        const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, "0");
        const day = String(adjustedDate.getUTCDate()).padStart(2, "0");
        const hoursFormatted = String(adjustedDate.getUTCHours()).padStart(2, "0");
        const minutesFormatted = String(adjustedDate.getUTCMinutes()).padStart(2, "0");
        const secondsFormatted = String(adjustedDate.getUTCSeconds()).padStart(2, "0");

        return `${year}-${month}-${day} ${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
    };
    const shortenTimezoneLabel = (label: string) => {
        const startIndex = label.indexOf("(");
        const endIndex = label.indexOf(")");
        if (startIndex !== -1 && endIndex !== -1) {
            return label.slice(endIndex + 1).trim();
        }
        return label;
    }



    const toggleDetails = async (id: string) => {
        if (expandedRow === id) {
            setExpandedRow(null)
        } else {
            setExpandedRow(id)
            setLoadingDetails(true);
            let temp = await onSelectLog(id);
            setLogDetails(temp);
            setLoadingDetails(false);

        }
    }

    useEffect(() => {
        let temp = getUtcOffsets();
        setTimezones(temp);
    }, [])

    useEffect(() => {
        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener("scroll", debouncedScrollHandler);
        }
        return () => {
            if (container) {
                container.removeEventListener("scroll", debouncedScrollHandler);
            }
            if (scrollTimeoutRef.current) {
                window.clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [reloadLogs, debouncedScrollHandler]);
    useEffect(() => {
        if (!reloadLogs && tableContainerRef.current && scrollPosition > 0) {
            // Use requestAnimationFrame for better timing after DOM updates
            // This ensures the DOM has fully updated before we try to restore scroll position
            requestAnimationFrame(() => {
                // Additional timeout for safety
                setTimeout(() => {
                    if (tableContainerRef.current) {
                        tableContainerRef.current.scrollTop = scrollPosition;
                    }
                }, 50);
            });
        }
    }, [reloadLogs, logs, scrollPosition]);

    const formatLogDetails = (logDetails: ConnectionLog | null, logLevel: number) => {
        if (!logDetails) return null;

        const isError = logLevel === 40;

        const formatTracebackValue = (value: any) => {
            if (typeof value === 'string') {
                return (
                    <pre className="whitespace-pre-wrap font-mono text-xs p-2 rounded-sm dark:bg-neutral-gray-700 dark:text-neutral-gray-100 transition-colors duration-300">
                        {value}
                    </pre>
                );
            }
            return JSON.stringify(value);
        };

        const formatJsonValue = (value: any) => {
            return (
                <span className="break-all whitespace-normal">
                    {JSON.stringify(value)}
                </span>
            );
        };

        return (
            <div className="w-full">
                <div className="text-secondary-gray dark:text-neutral-gray-300 font-mono text-xs break-words scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent">
                    {Object.entries(logDetails).map(([key, value]) => {
                        const isErrorField = isError && (
                            key === 'error' ||
                            (key === 'details' && value && typeof value === 'object' && 'error' in value)
                        );

                        return key !== 'details' ? (
                            <div key={key} className={`mb-1 ${isErrorField ? 'text-logs-orange bg-very-light-orange dark:bg-orange-900 dark:bg-opacity-30 dark:text-orange-300' : ''} transition-colors duration-300`}>
                                <span className="font-semibold">{key}:</span> {formatJsonValue(value)}
                            </div>
                        ) : (
                            <div key={key} className="mb-1">
                                <span className="font-semibold">details:</span>
                                <div className="pl-4">
                                    {Object.entries(logDetails.details).map(([detailKey, detailValue]) => {
                                        const isDetailError = isError && (
                                            detailKey === 'error' ||
                                            detailKey.includes('error') || detailKey.includes('server_response_body') || detailKey.includes('server_response_status') ||
                                            (typeof detailValue === 'string' && detailValue.toLowerCase().includes('error'))
                                        );

                                        const isTraceback = detailKey === 'error_traceback';

                                        return (
                                            <div key={detailKey} className={isDetailError ? 'text-logs-orange dark:text-orange-300 transition-colors duration-300' : ''}>
                                                <span className={isDetailError ? "font-semibold bg-very-light-orange dark:bg-orange-900 dark:bg-opacity-30 transition-colors duration-300" : "font-semibold"}>{detailKey}:</span>{" "}
                                                {isTraceback
                                                    ? formatTracebackValue(detailValue)
                                                    : formatJsonValue(detailValue)
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="">
            <div className="flex flex-col w-full mt-8">

                <LogsFilter
                    filters={filters}
                    globalSearchStr={globalSearchStr}
                    timezones={timezones}
                    selectedTimezone={selectedTimezone}
                    onChangeGlobalSearch={onChangeLogGlobalSearch}
                    onRemoveTag={onRemoveLogFilterTag}
                    onResetFilters={onResetLogFilters}
                    onChangeStatusFilter={onChangeLogStatus}
                    onChangeLogType={onChangeLogType}
                    onChangeLogDate={onChangeLogDate}
                    onSelectTimezone={onSelectTimezone}
                />
                <div className="w-full h-auto mb-4 scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600">
                    <div className="flex justify-end mb-2">
                        <div
                            onClick={() => onRefreshLogs()}
                            className={`flex justify-center items-center w-8 h-8 rounded-md cursor-pointer hover:bg-light-gray dark:hover:bg-neutral-gray-700 ${reloadLogs ? 'animate-spin origin-center' : ''} transition-colors duration-300`}
                        >
                            <RefreshSpinner />
                        </div>
                    </div>
                    <div
                        ref={tableContainerRef}
                        className={`flex flex-col w-full h-fit max-h-[calc(100vh-320px)] overflow-y-auto will-change-scroll scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600 ${tableClasses.container}`}>
                        <table className="table-fixed w-full cursor-default">
                            <thead>
                                <tr className={tableClasses.header}>
                                    <th className={`${tableClasses.headerCell} pl-4`}></th>
                                    <th className={`${tableClasses.headerCell} w-1/12`}>{t("Level")}</th>
                                    <th className={tableClasses.headerCell + " w-2/12"}>
                                        <span onClick={() => {
                                            requestSort(sortDirection);
                                            setSortDirection(!sortDirection);
                                        }} className={tableClasses.sortIcon}>
                                            {t("Timestamp")} ({shortenTimezoneLabel(timezone.label)}) {sortDirection ? <SortedUp /> : <SortedDown />}
                                        </span>
                                    </th>
                                    <th className={`${tableClasses.headerCell} w-8/12 cursor-pointer`}>{t("Title")}</th>
                                </tr>
                            </thead>
                            {(logs.length === 0 || !connectionHasDestinations) && !reloadLogs && <tbody className="text-sm w-full">
                                <tr>
                                    <td colSpan={4} className="text-center py-8">
                                        {(skippedAddingDestinations || !connectionHasDestinations) ?
                                            <>
                                                <NoResults
                                                    headerMsg={"This Connection needs at least one Destination"}
                                                    labelMsg={<>
                                                        <span
                                                            className="text-bright-green dark:text-dark-green-100 underline cursor-pointer transition-colors duration-300"
                                                            onClick={() => navigate("/connections/" + connection_id + "/destinations")}
                                                        >
                                                            Connect a Destination
                                                        </span> to see data flow in the logs
                                                    </>}
                                                />
                                                <a
                                                    href="https://support.earthranger.com/en_US/gundi"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="bg-dark-green dark:bg-dark-green-800 px-4 py-2 font-bold text-white rounded-sm mt-2 cursor-pointer inline-block text-center transition-colors duration-300 hover:bg-dark-green-800 dark:hover:bg-dark-green-700"
                                                >{t("Help Center")}</a>
                                            </>
                                            : logsError ?
                                                <NoResults
                                                    headerMsg={t("Failed to Load Logs" as any)}
                                                    labelMsg={t("Unable to load activity logs. Please try again later." as any)}
                                                />
                                                :
                                                <NoResults
                                                    headerMsg={t("No Results Found")}
                                                    labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                                />}
                                    </td>
                                </tr>
                            </tbody>}
                            {logs.length >= 1 && connectionHasDestinations && <tbody className="text-sm w-full">
                                {logs.map((log: ConnectionLog) => (
                                    <React.Fragment key={log.id}>
                                        <tr
                                            key={log.id}
                                            className={`${tableClasses.row} border-t cursor-pointer`}
                                            onClick={() => toggleDetails(log.id)}
                                        >
                                            <td
                                                className={`${tableClasses.cell} pl-4 cursor-pointer font-semibold text-dark-green dark:text-dark-green-100 transition-colors duration-300`}
                                                onClick={() => toggleDetails(log.id)}
                                            >
                                                {expandedRow === log.id ? <ArrowDown /> : <ArrowRight />}
                                            </td>
                                            <td className={`${tableClasses.cell} text-left`}>
                                                <p className={getClassname(log.log_level)}>
                                                    {getLogLevelText(log.log_level)}
                                                </p>
                                            </td>
                                            <td className={`${tableClasses.cell} text-left text-secondary-gray dark:text-neutral-gray-300 transition-colors duration-300`}>
                                                {formatDatetime(log.created_at)}
                                            </td>
                                            <td className={`${tableClasses.cell} text-left w-8/12`}>{log.title}</td>
                                        </tr>
                                        {expandedRow === log.id && (
                                            <tr>
                                                <td className={tableClasses.cell}></td>
                                                <td className={tableClasses.cell}></td>
                                                <td className={tableClasses.cell}></td>
                                                <td className={`${tableClasses.cell} pb-2 w-8/12 text-secondary-gray dark:text-neutral-gray-300 text-xs text-wrap`}>
                                                    {loadingDetails ? <Spinner /> : formatLogDetails(logDetails, log.log_level)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>}
                        </table>
                    </div>
                    {reloadLogs && <div className="relative w-full h-1 bg-gray-200 dark:bg-neutral-gray-700 overflow-hidden mt-2 transition-colors duration-300">
                        <div className="absolute top-0 left-0 w-1/3 h-full bg-dark-green dark:bg-dark-green-100 animate-loading transition-colors duration-300"></div>
                    </div>}
                </div>
            </div>
        </div>
    )
};

export default ConnectionDetailsLog;